import { apiFetch, apiUpload, apiDownloadNamed } from "@/lib/apiClient";
import { saveBlob } from "@/utils/download";
import { odata } from "@/lib/odata";
import {
  pageQuery,
  toPaged,
  toStatusSummary,
  type Paged,
  type PageParams,
  type StatusSummary,
} from "@/lib/paging";
import { toUserRef } from "@/utils/userRef";

export interface PartnerFormItem {
  id: string;
  submittedAt: string;
  status: string;
  targetUserId: string | null;
  targetUserName: string | null;
  processedUserId: string | null;
  contactName: string | null;
  ownershipType: string | null;
  ssn: string | null;
  legalName: string | null;
  email: string | null;
  businessPhone: string | null;
  ein: string | null;
  addressLine1: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  hasLicense: boolean;
  hasVoidCheck: boolean;
}

export interface PartnerFormPrefillUser {
  email: string | null;
  name: string | null;
  status: string | null;
  hasDriverLicense: boolean;
  hasVoidCheck: boolean;
  driverLicenseName: string | null;
  voidCheckName: string | null;
}

export interface PartnerFormPrefillParty {
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  fullName: string | null;
  phone: string | null;
  email: string | null;
  dateOfBirth: string | null;
  ownershipType: string | null;
  ssn: string | null;
  ein: string | null;
  legalName: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  bankRoutingNumber: string | null;
  bankAccountNumber: string | null;
}

export interface PartnerFormPrefillForm {
  id: string;
  status: string;
  submittedAt: string;
  processedAt: string | null;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  dateOfBirth: string | null;
  ownershipType: string | null;
  ssn: string | null;
  ein: string | null;
  legalName: string | null;
  email: string | null;
  businessPhone: string | null;
  addressLine1: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  hasDriverLicense: boolean;
  hasVoidCheck: boolean;
  driverLicenseName: string | null;
  voidCheckName: string | null;
}

export interface PartnerFormPrefill {
  valid: boolean;
  expired: boolean;
  isNewPartner: boolean;
  linkExpiresAt: string | null;
  user: PartnerFormPrefillUser | null;
  party: PartnerFormPrefillParty | null;
  currentForm: PartnerFormPrefillForm | null;
}

export interface PartnerLink {
  token: string;
  path: string;
  forNewPartner: boolean;
  expiresAt: string | null;
}

export interface PartnerFormSubmitResult {
  submitted: boolean;
  formId: string;
  status: string;
  emailApplied?: boolean;
  emailAlreadyInUse?: boolean;
}

interface PartnerFormListResponse {
  id: string;
  targetUserId: string | null;
  targetUserName: string | null;
  status: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  businessPhone: string | null;
  ssn: string | null;
  ein: string | null;
  legalName: string | null;
  hasDriverLicense: boolean;
  hasVoidCheck: boolean;
  submittedAt: string;
  processedUserId: string | null;
}

function toPartnerFormItem(r: PartnerFormListResponse): PartnerFormItem {
  const contactName = [r.firstName, r.lastName].filter(Boolean).join(" ") || null;
  return {
    id: r.id,
    submittedAt: r.submittedAt,
    status: r.status,
    targetUserId: r.targetUserId ?? null,
    targetUserName: r.targetUserName ?? null,
    processedUserId: r.processedUserId ?? null,
    contactName,
    ownershipType: null,
    ssn: r.ssn ?? null,
    legalName: r.legalName ?? null,
    email: r.email ?? null,
    businessPhone: r.businessPhone ?? null,
    ein: r.ein ?? null,
    addressLine1: null,
    city: null,
    state: null,
    zip: null,
    hasLicense: r.hasDriverLicense ?? false,
    hasVoidCheck: r.hasVoidCheck ?? false,
  };
}

const PARTNER_FORM_SEARCH_COLS = ["firstName", "lastName", "legalName", "email"];

export const PARTNER_FORM_STATUS_TABS = ["Pending", "Processed"];

function partnerFormExpr(p: PageParams & { since?: string }): string[] {
  return [
    ...(p.expr ?? []),
    ...(p.since?.trim() ? [odata.ge("submittedAt", p.since.trim())] : []),
  ];
}

export const partnerForms = {
  async list(params: PageParams & { since?: string } = {}): Promise<Paged<PartnerFormItem>> {
    const res = await apiFetch<Paged<PartnerFormListResponse> | PartnerFormListResponse[]>(
      `/partner-forms${pageQuery(
        { ...params, expr: partnerFormExpr(params) },
        undefined,
        PARTNER_FORM_SEARCH_COLS,
      )}`,
    );
    const p = toPaged<PartnerFormListResponse>(res);
    return { ...p, items: p.items.map(toPartnerFormItem) };
  },
  async summary(params: PageParams & { since?: string } = {}): Promise<StatusSummary> {
    const res = await apiFetch<unknown>(
      `/partner-forms/summary${pageQuery(
        { ...params, status: undefined, pageNumber: undefined, pageSize: undefined, expr: partnerFormExpr(params) },
        undefined,
        PARTNER_FORM_SEARCH_COLS,
      )}`,
    );
    return toStatusSummary(res);
  },
  link(userId: string | null) {
    const qs = userId ? `?userId=${encodeURIComponent(userId)}` : "";
    return apiFetch<PartnerLink>(`/partner-forms/link${qs}`);
  },
  regenerateLink(userId: string | null) {
    return apiFetch<PartnerLink>("/partner-forms/regenerate-link", {
      method: "POST",
      body: JSON.stringify({ userId: userId ?? null }),
    });
  },
  sendLink(opts: { userId?: string | null; email?: string; toName?: string | null }) {
    return apiFetch<{ sent: boolean; to: string }>("/partner-forms/send-link", {
      method: "POST",
      body: JSON.stringify({
        userId: opts.userId ?? null,
        email: opts.email ?? null,
        toName: opts.toName ?? null,
      }),
    });
  },
  createUser(id: string) {
    return apiFetch<{ userId: string; userNumber: number; tempPassword: string }>(
      `/partner-forms/${encodeURIComponent(id)}/create-user`,
      { method: "POST" },
    );
  },
  documentFile(id: string, which: "license" | "check") {
    return apiDownloadNamed(
      `/partner-forms/${encodeURIComponent(id)}/documents/${which}`,
    );
  },
};

export type PartnerFormBankScan = Pick<
  OnboardingScanResult,
  "routingNumber" | "accountNumber" | "bankName"
>;

export type PartnerFormLicenseScan = Pick<
  OnboardingScanResult,
  "firstName" | "middleName" | "lastName" | "dateOfBirth" | "addressLine1" | "city" | "state" | "zip"
>;

export const SCAN_TIMEOUT_MS = 60_000;

function scanDocument<T>(file: File, type: "driverLicense" | "voidedCheck") {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("type", type);
  return apiUpload<T>("/public/onboarding/scan", fd, "POST", { timeout: SCAN_TIMEOUT_MS });
}

export const publicPartnerForm = {
  scanVoidedCheck(file: File) {
    return scanDocument<PartnerFormBankScan>(file, "voidedCheck");
  },
  scanDriverLicense(file: File) {
    return scanDocument<PartnerFormLicenseScan>(file, "driverLicense");
  },
  prefill(token: string) {
    return apiFetch<PartnerFormPrefill>(
      `/public/partner-form/prefill?token=${encodeURIComponent(token)}`,
    );
  },
  submit(body: Record<string, string | null | undefined>) {
    return apiFetch<PartnerFormSubmitResult>("/public/partner-form", {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
};

export type ShareLinkSource = "APPLY" | "VERIFICATION";

export interface ShareLinkResolution {
  valid: boolean;
  agentNumber: number | null;
  agentName: string | null;
  campaignId: string | null;
  campaignName: string | null;
}

interface ShareLinkResolveResponse {
  valid: boolean;
  agentNumber: number | string | null;
  agentName: string | null;
  campaignId: string | null;
  campaignName: string | null;
}

export type PricedLinkState = "Open" | "Used" | "Expired" | "Cancelled";

export interface PricedLink {
  id: string;
  code: string;
  path: string;
  agentNumber: number;
  label: string | null;
  pricing: Record<string, string | number | boolean | null>;
  state: PricedLinkState;
  createdAt: string;
  expiresAt: string;
  usedAt: string | null;
  applicationId: string | null;
  applicationDba: string | null;
}

export interface PricedLinkSave {
  agentNumber: number;
  label: string | null;
  pricing: Record<string, unknown>;
}

export const pricedLinks = {
  list(agentNumber: number) {
    return apiFetch<PricedLink[]>(`/onboarding/priced-links?agentNumber=${encodeURIComponent(agentNumber)}`);
  },
  create(body: PricedLinkSave) {
    return apiFetch<PricedLink>("/onboarding/priced-links", { method: "POST", body: JSON.stringify(body) });
  },
  update(id: string, body: PricedLinkSave) {
    return apiFetch<PricedLink>(`/onboarding/priced-links/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },
  cancel(id: string) {
    return apiFetch<PricedLink>(`/onboarding/priced-links/${encodeURIComponent(id)}/cancel`, { method: "POST" });
  },
};

export const publicOnboarding = {
  async resolvePriced(code: string): Promise<ShareLinkResolution> {
    const r = await apiFetch<{ valid: boolean; agentName: string | null }>(
      `/public/onboarding/priced/${encodeURIComponent(code)}`,
    );
    return { valid: !!r.valid, agentNumber: null, agentName: r.agentName ?? null, campaignId: null, campaignName: null };
  },
  async resolve(code: string, source: ShareLinkSource): Promise<ShareLinkResolution> {
    const qs = new URLSearchParams({ code, source });
    const r = await apiFetch<ShareLinkResolveResponse>(
      `/public/onboarding/resolve?${qs.toString()}`,
    );
    const agentNumber = r.agentNumber == null ? null : Number(r.agentNumber);
    return {
      valid: !!r.valid,
      agentNumber: agentNumber == null || Number.isNaN(agentNumber) ? null : agentNumber,
      agentName: r.agentName ?? null,
      campaignId: r.campaignId ?? null,
      campaignName: r.campaignName ?? null,
    };
  },
};

export interface OnboardingSubmissionDoc {
  id: string;
  documentType: string;
}

export interface OnboardingSubmissionRow {
  id: string;
  submittedAt: string;
  dbaName: string | null;
  legalName: string | null;
  contactName: string | null;
  email: string | null;
  phone: string | null;
  status: string;
  agentName: string | null;
  agentNumber: number | null;
  documents: OnboardingSubmissionDoc[];
}

export interface OnboardingAgent {
  id: string;
  userNumber: number;
  name: string;
}

export interface OnboardingScanResult {
  type?: string | null;
  routingNumber?: string | null;
  accountNumber?: string | null;
  bankName?: string | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  dateOfBirth?: string | null;
  licenseNumber?: string | null;
  addressLine1?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  expirationDate?: string | null;
  fields?: Record<string, string | null> | null;
}

export type OnboardingSubmissionSource = "PUBLIC" | "VERIFICATION";

function toOnboardingSubmissionRow(r: Record<string, unknown>): OnboardingSubmissionRow {
  const owner = r.owner ? toUserRef(r.owner) : null;
  return {
    id: r.id as string,
    submittedAt: (r.submittedAt as string) ?? "",
    dbaName: (r.dbaName as string) ?? null,
    legalName: (r.legalName as string) ?? null,
    contactName: (r.contactName as string) ?? null,
    email: (r.email as string) ?? null,
    phone: (r.phone as string) ?? null,
    status: (r.status as string) ?? "",
    agentName: owner?.name || null,
    agentNumber: owner?.userNumber ?? null,
    documents: (r.documents as OnboardingSubmissionDoc[]) ?? [],
  };
}

const SUBMISSION_SEARCH_COLS = ["dbaName", "legalName", "contactName", "email"];

export const UNASSIGNED_AGENT = "unassigned";

export const SUBMISSION_STATUS_GROUPS: Record<string, string[]> = {
  Submitted: ["AwaitingAgentPricing", "New", "Submitted", "Pending", "ESignSent", "ESigned", "ActionNeeded", "Underwriting"],
  Approved: ["Approved", "Boarded", "Live"],
  Rejected: ["Declined", "Cancelled"],
};

interface SubmissionParams extends PageParams {
  source?: string;
  agentId?: string;
  since?: string;
}

function submissionExpr(p: SubmissionParams): string[] {
  const tab = p.status?.trim();
  const group = tab ? SUBMISSION_STATUS_GROUPS[tab] : undefined;
  return [
    ...(p.expr ?? []),
    ...(group
      ? [odata.or(...group.map((s) => odata.eq("status", s)))]
      : tab
        ? [odata.eq("status", tab)]
        : []),
    ...(p.agentId?.trim()
      ? [p.agentId.trim() === UNASSIGNED_AGENT
          ? odata.eq("userId", null)
          : odata.eq("userId", p.agentId.trim())]
      : []),
    ...(p.since?.trim() ? [odata.ge("submittedAt", p.since.trim())] : []),
  ];
}

export const onboarding = {

  async agents(params: PageParams = {}): Promise<Paged<OnboardingAgent>> {
    const res = await apiFetch<Paged<OnboardingAgent> | OnboardingAgent[]>(
      `/onboarding/agents${pageQuery(params, { keyword: params.keyword?.trim() })}`,
    );
    return toPaged<OnboardingAgent>(res);
  },

  async exportLinks(format: "csv" | "xlsx") {
    const { blob, filename } = await apiDownloadNamed(
      `/onboarding/export-links?format=${format}`,
    );
    saveBlob(
      blob,
      filename ?? (format === "xlsx" ? "share-links.xlsx" : "share-links.csv"),
    );
  },
  sendLink(
    agentNumber: number,
    to: string,
    toName?: string | null,
    source?: ShareLinkSource,
    pricedLinkCode?: string | null,
  ) {
    return apiFetch<{ sent: boolean }>("/onboarding/send-link", {
      method: "POST",
      body: JSON.stringify({
        agentNumber, to, toName: toName ?? null, source: source ?? null, pricedLinkCode: pricedLinkCode ?? null,
      }),
    });
  },
  applyLink(agentNumber: number) {
    return apiFetch<{ agentNumber: number; code: string; path: string }>(
      `/onboarding/apply-link?agentNumber=${encodeURIComponent(agentNumber)}`,
    );
  },
  regenerateCode(agentNumber: number) {
    return apiFetch<{ agentNumber: number; code: string; path: string }>(
      "/onboarding/regenerate-code",
      { method: "POST", body: JSON.stringify({ agentNumber }) },
    );
  },
  async submissions(params: SubmissionParams = {}): Promise<Paged<OnboardingSubmissionRow>> {
    const res = await apiFetch<Paged<Record<string, unknown>> | Record<string, unknown>[]>(
      `/onboarding/submissions${pageQuery(
        { ...params, status: undefined, expr: submissionExpr(params) },
        { source: params.source ?? "PUBLIC" },
        SUBMISSION_SEARCH_COLS,
      )}`,
    );
    const p = toPaged<Record<string, unknown>>(res);
    return { ...p, items: p.items.map(toOnboardingSubmissionRow) };
  },
  async submissionsSummary(params: SubmissionParams = {}): Promise<StatusSummary> {
    const res = await apiFetch<unknown>(
      `/onboarding/submissions/summary${pageQuery(
        {
          ...params,
          status: undefined,
          pageNumber: undefined,
          pageSize: undefined,
          expr: submissionExpr({ ...params, status: undefined }),
        },
        { source: params.source ?? "PUBLIC" },
        SUBMISSION_SEARCH_COLS,
      )}`,
    );
    return toStatusSummary(res);
  },
  scan(file: File, type: "driverLicense" | "voidedCheck") {
    return scanDocument<OnboardingScanResult>(file, type);
  },
};
